import type { PropsWithChildren } from 'react'

import { pastDateValidatorOptional } from 'app/components/forms/inputDate_past/inputDate_past'
import RentalReferenceInputDate from 'app/match/rental_reference/components/RentalReferenceInputDate'

/**
 * Optional past date input for Rental Reference Last Inspected Date
 * Uses custom RentalReferenceInputDate with MM/YYYY format support
 * ONLY used in rental reference form
 */

// Type definitions for validation
type ValidationError = string
type ValidationResult = ValidationError[]

// Flattens validation result array to single string (first error or empty string)
const firstElementOrEmptyString = (arr: ValidationResult): ValidationError =>
  arr.length > 0 ? arr[0]! : ''

// Flattened validator that returns single error string
const validatePastDateOptionalFlattened = (date: string): ValidationError =>
  firstElementOrEmptyString(pastDateValidatorOptional(date))

// Type definitions for HOC pattern
interface OnChangePayload {
  value: string
  error: ValidationError
}

interface BaseComponentProps {
  value: string
  onChange: (payload: OnChangePayload) => void
  validate: (value: string) => ValidationError
}

type ValidatedComponentProps<T> = Omit<T, 'validate'> & {
  value: string
  onChange: (payload: OnChangePayload) => void
}

// Higher-Order Component for validation
function withValidation<P extends BaseComponentProps>(
  Component: React.ComponentType<P>,
  validate: (value: string) => ValidationError,
) {
  return function ValidatedComponent(
    props: PropsWithChildren<ValidatedComponentProps<P>>,
  ) {
    const { value, onChange, ...restProps } = props

    const handleChange = ({ value: newValue, error }: OnChangePayload) => {
      const validationError = error || validate(newValue)
      onChange({ value: newValue, error: validationError })
    }

    return (
      <Component
        {...(restProps as P)}
        value={value}
        onChange={handleChange}
        validate={validate}
      />
    )
  }
}

export const RentalReferenceInputPastDateOptional = withValidation(
  RentalReferenceInputDate,
  validatePastDateOptionalFlattened,
)
